import noScroll from "@/utils/noScroll.js";
import isTouchDevice from "@/utils/isTouchDevice.js";

/**
 * Виджет всплывающего окна «Мобильное приложение», привязанного к якорю.
 *
 * Поведение зависит от устройства:
 *   - на десктопе (нет сенсорного ввода) по якорю открывается всплывающее
 *     окно с QR-кодом и описанием (контент свёрстан в шаблоне);
 *   - на сенсорном устройстве переход по якорю окно не открывает, а сразу
 *     ведёт на ссылку мобильного приложения (data-mobile-link).
 *
 * Логика срабатывания якоря повторяет anchorPopup: внешний переход
 * (проверка при инициализации и по событию hashchange) и клик по ссылке на
 * этот якорь в пределах текущей страницы (hashchange при уже стоящем якоре не
 * срабатывает, поэтому такие клики перехватываются отдельно).
 *
 * Закрытие окна (только на десктопе): крестик (data-popup-close), клик по фону
 * или клавиша Esc. При закрытии якорь убирается из адреса, чтобы повторный
 * переход по той же ссылке снова открыл окно.
 */
export default function anchorMobileApp(element, data) {
    const anchor = data.anchor;
    if (!anchor) {
        return;
    }

    const isTouch = isTouchDevice();
    const mobileLink = data.mobileLink;
    const openClass = data.class || "open";
    const closeSelector = data.closeSelector || "[data-popup-close]";
    const hash = "#" + anchor;

    let isOpen = false;

    // Реакция на якорь: на сенсорном устройстве — переход на ссылку приложения,
    // на десктопе — открытие всплывающего окна.
    function activate() {
        if (isTouch) {
            if (mobileLink) {
                window.location.href = mobileLink;
            }
            return;
        }
        open();
    }

    function open() {
        if (isOpen) {
            return;
        }
        isOpen = true;
        element.classList.add(openClass);
        noScroll.hideScroll();
    }

    function close() {
        if (!isOpen) {
            return;
        }
        isOpen = false;
        element.classList.remove(openClass);
        noScroll.showScroll();

        // Убираем якорь из адреса, не оставляя записи в истории и не
        // прокручивая страницу, чтобы по этой же ссылке окно открылось снова.
        if (window.location.hash === hash) {
            history.replaceState(null, "", window.location.pathname + window.location.search);
        }
    }

    function syncFromHash() {
        if (window.location.hash === hash) {
            activate();
        }
    }

    // Закрытие по крестику и клику по фону (вне окна).
    element.addEventListener("click", function (event) {
        if (event.target === element || event.target.closest(closeSelector)) {
            close();
        }
    });

    // Закрытие по Esc.
    document.addEventListener("keydown", function (event) {
        if (event.key === "Escape" && isOpen) {
            close();
        }
    });

    // Переход внутри страницы: клик по ссылке на наш якорь. Реагируем только на
    // ссылки в пределах текущей страницы — переходы на другие страницы
    // выполняются как обычно, реакция там сработает при загрузке.
    document.addEventListener("click", function (event) {
        const link = event.target.closest("a");
        if (!link) {
            return;
        }

        const url = new URL(link.href, window.location.href);
        if (url.hash !== hash || url.pathname !== window.location.pathname || url.host !== window.location.host) {
            return;
        }

        event.preventDefault();

        // На сенсорном устройстве сразу уводим на ссылку приложения, не трогая
        // адрес текущей страницы.
        if (isTouch) {
            activate();
            return;
        }

        // Фиксируем якорь в адресе (без прокрутки), если его там ещё нет.
        if (window.location.hash !== hash) {
            history.pushState(null, "", hash);
        }

        open();
    });

    // Обычный/внешний переход по якорю уже открытой страницы.
    window.addEventListener("hashchange", syncFromHash);

    // Внешний переход: страница сразу открыта с нужным якорем.
    syncFromHash();
}
